// src/pages/ScannerPage.jsx
import React, { useState, useEffect } from 'react';
import Scanner from '../components/Scanner';
import ManualCodeEntry from '../components/ManualCodeEntry';
import VouchersDatabase from '../components/VouchersDatabase';
import Dashboard from '../components/Dashboard';
import CustomersAnalysis from '../components/CustomersAnalysis';
import UserManagement from '../components/UserManagement';
import Reports from '../components/Reports';
import SystemLogs from '../components/SystemLogs';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || "http://localhost:3004";

const ScannerPage = ({ token }) => {
  const [activeView, setActiveView] = useState('home');
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Pobierz informacje o obecnym użytkowniku
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentUser(response.data);
      } catch (error) {
        console.error('Error fetching current user:', error);
        // Jeśli token jest nieprawidłowy, wyloguj użytkownika
        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem('token');
          window.location.reload();
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUser();
  }, [token]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      window.location.reload();
    }
  };

  // Sprawdź uprawnienia do danej funkcji
  const hasPermission = (permission) => {
    if (!currentUser) return false;
    
    const permissions = {
      'customers_access': ['kierownik', 'admin'],
      'user_management': ['admin'],
      'voucher_management': ['kierownik', 'admin'],
      'dashboard_access': ['pracownik', 'kierownik', 'admin'],
      'reports_access': ['kierownik', 'admin'],
      'system_logs_access': ['admin']
    };

    const allowedRoles = permissions[permission];
    return allowedRoles ? allowedRoles.includes(currentUser.role) : false;
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      'pracownik': 'Pracownik',
      'kierownik': 'Kierownik', 
      'admin': 'Administrator'
    };
    return roleLabels[role] || role;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <p className="ml-3 text-gray-600">Ładowanie...</p>
      </div>
    );
  }

  const renderHomeView = () => (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* User Info & Logout Button - All devices */}
        <div className="flex justify-between items-center mb-6 pt-safe">
          <div className="text-sm">
            <div className="font-medium text-gray-900">{currentUser?.fullName}</div>
            <div className="text-gray-500">{getRoleLabel(currentUser?.role)}</div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 bg-red-100 text-red-700 px-4 py-2 rounded-xl hover:bg-red-200 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className="text-sm font-medium">Wyloguj</span>
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 w-16 h-16 sm:w-20 sm:h-20 rounded-full mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Panel Voucherów</h1>
          <p className="text-gray-600 text-sm">Zarządzaj voucherami w prosty sposób</p>
        </div>

        {/* Action Cards - Responsive Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Sekcja podstawowa - Skanowanie i wprowadzanie */}
          <div className="sm:col-span-2 lg:col-span-3">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">
              Skanowanie voucherów
            </div>
          </div>
          
          <div 
            onClick={() => setActiveView('scanner')}
            className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100 active:scale-95 transition-all duration-150 cursor-pointer hover:shadow-xl"
          >
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="bg-blue-100 p-2 sm:p-3 rounded-xl flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h4M4 4h5.01M4 20h5.01" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-800 text-sm sm:text-base">Skanuj QR Code</h3>
                <p className="text-gray-500 text-xs sm:text-sm truncate">Użyj kamery do skanowania</p>
              </div>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          <div 
            onClick={() => setActiveView('manual')}
            className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100 active:scale-95 transition-all duration-150 cursor-pointer hover:shadow-xl"
          >
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="bg-green-100 p-2 sm:p-3 rounded-xl flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-800 text-sm sm:text-base">Wprowadź kod</h3>
                <p className="text-gray-500 text-xs sm:text-sm truncate">Wpisz kod vouchera ręcznie</p>
              </div>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          {/* Separator */}
          <div className="sm:col-span-2 lg:col-span-3">
            <div className="border-t border-gray-200 my-4"></div>
          </div>

          {/* Sekcja przeglądania danych */}
          <div className="sm:col-span-2 lg:col-span-3">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">
              Przegląd danych
            </div>
          </div>

          <div 
            onClick={() => setActiveView('dashboard')}
            className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100 active:scale-95 transition-all duration-150 cursor-pointer hover:shadow-xl"
          >
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="bg-orange-100 p-2 sm:p-3 rounded-xl flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-800 text-sm sm:text-base">Dashboard</h3>
                <p className="text-gray-500 text-xs sm:text-sm truncate">Przegląd statystyk i aktywności</p>
              </div>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          <div 
            onClick={() => setActiveView('database')}
            className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100 active:scale-95 transition-all duration-150 cursor-pointer hover:shadow-xl"
          >
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="bg-blue-100 p-2 sm:p-3 rounded-xl flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-800 text-sm sm:text-base">Baza danych voucherów</h3>
                <p className="text-gray-500 text-xs sm:text-sm truncate">Szczegółowy przegląd z danymi klientów</p>
              </div>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          {/* Separator dla funkcji zaawansowanych */}
          {(hasPermission('customers_access') || hasPermission('user_management') || hasPermission('reports_access')) && (
            <>
              {/* Separator */}
              <div className="sm:col-span-2 lg:col-span-3">
                <div className="border-t border-gray-200 my-4"></div>
              </div>
              
              {/* Sekcja zarządzania - tylko dla kierownik i admin */}
              <div className="sm:col-span-2 lg:col-span-3">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">
                  Zarządzanie i analizy
                </div>
              </div>

              {/* Karta analizy klientów - tylko dla kierownik i admin */}
              {hasPermission('customers_access') && (
                <div 
                  onClick={() => setActiveView('customers')}
                  className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100 active:scale-95 transition-all duration-150 cursor-pointer hover:shadow-xl"
                >
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="bg-indigo-100 p-2 sm:p-3 rounded-xl flex-shrink-0">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base">Analiza klientów</h3>
                      <p className="text-gray-500 text-xs sm:text-sm truncate">Stali klienci i statystyki zakupów</p>
                    </div>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              )}

              {/* Karta raportów - tylko dla kierownik i admin */}
              {hasPermission('reports_access') && (
                <div 
                  onClick={() => setActiveView('reports')}
                  className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100 active:scale-95 transition-all duration-150 cursor-pointer hover:shadow-xl"
                >
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="bg-green-100 p-2 sm:p-3 rounded-xl flex-shrink-0">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base">Raporty sprzedaży</h3>
                      <p className="text-gray-500 text-xs sm:text-sm truncate">Analizy i statystyki wykorzystania voucherów</p>
                    </div>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              )}

              {/* Karta zarządzania użytkownikami - tylko dla admin */}
              {hasPermission('user_management') && (
                <div 
                  onClick={() => setActiveView('users')}
                  className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100 active:scale-95 transition-all duration-150 cursor-pointer hover:shadow-xl"
                >
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="bg-purple-100 p-2 sm:p-3 rounded-xl flex-shrink-0">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base">Zarządzanie użytkownikami</h3>
                      <p className="text-gray-500 text-xs sm:text-sm truncate">Dodaj i edytuj użytkowników</p>
                    </div>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              )}

              {hasPermission('system_logs_access') && (
                <div
                  onClick={() => setActiveView('systemLogs')}
                  className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100 active:scale-95 transition-all duration-150 cursor-pointer hover:shadow-xl"
                >
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="bg-red-100 p-2 sm:p-3 rounded-xl flex-shrink-0">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M5.636 5.636l12.728 12.728M4.929 19.071l14.142-14.142" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base">Logi systemowe</h3>
                      <p className="text-gray-500 text-xs sm:text-sm truncate">Podgląd zdarzeń WooCommerce, Fakturownia i e-maili</p>
                    </div>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Dodatkowe padding na dole dla mobile */}
        <div className="pb-safe h-8"></div>
      </div>
    </div>
  );

  const renderBackButton = () => (
    <div className="sticky top-0 bg-white/80 backdrop-blur-sm border-b border-gray-200 z-50 safe-area-top">
      <div className="container mx-auto px-4 py-3 max-w-4xl">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => setActiveView('home')}
            className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl transition-all duration-150 active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-medium text-sm">Panel główny</span>
          </button>
          
          {/* User info w nagłówku dla większych ekranów */}
          <div className="hidden sm:flex items-center space-x-4">
            <div className="text-right text-sm">
              <div className="font-medium text-gray-900">{currentUser?.fullName}</div>
              <div className="text-gray-500">{getRoleLabel(currentUser?.role)}</div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 bg-red-100 text-red-700 px-3 py-2 rounded-lg hover:bg-red-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="text-sm font-medium hidden md:inline">Wyloguj</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {activeView === 'home' && renderHomeView()}
      
      {activeView === 'scanner' && (
        <>
          {renderBackButton()}
          <div className="container mx-auto px-4 py-6 max-w-4xl">
            <Scanner token={token} currentUser={currentUser} />
          </div>
        </>
      )}

      {activeView === 'manual' && (
        <>
          {renderBackButton()}
          <div className="container mx-auto px-4 py-6 max-w-4xl">
            <ManualCodeEntry token={token} currentUser={currentUser} />
          </div>
        </>
      )}

      {activeView === 'database' && (
        <>
          {renderBackButton()}
          <div className="container mx-auto px-4 py-6 max-w-6xl">
            <VouchersDatabase token={token} />
          </div>
        </>
      )}

      {activeView === 'customers' && (
        <>
          {renderBackButton()}
          <div className="container mx-auto px-4 py-6 max-w-6xl">
            <CustomersAnalysis token={token} />
          </div>
        </>
      )}

      {activeView === 'dashboard' && (
        <>
          {renderBackButton()}
          <div className="container mx-auto px-4 py-6 max-w-6xl">
            <Dashboard token={token} />
          </div>
        </>
      )}

      {activeView === 'users' && (
        <>
          {renderBackButton()}
          <div className="container mx-auto px-4 py-6 max-w-6xl">
            <UserManagement token={token} currentUser={currentUser} />
          </div>
        </>
      )}

      {activeView === 'reports' && (
        <>
          {renderBackButton()}
          <div className="container mx-auto px-4 py-6 max-w-6xl">
            <Reports token={token} currentUser={currentUser} />
          </div>
        </>
      )}

      {activeView === 'systemLogs' && (
        <>
          {renderBackButton()}
          <div className="container mx-auto px-4 py-6 max-w-6xl">
            <SystemLogs token={token} />
          </div>
        </>
      )}
    </div>
  );
};

export default ScannerPage;
